// src/components/artwork/tabs/ArtworkComparables.jsx
//
// Onglet "Expertise" sur la fiche œuvre (ArtworkEdit)
// Permet à l'Art Advisor de saisir :
//   - 3 ventes comparables CUP
//   - {t("artworkComparables.expertValueSection")} + fourchette ±10%
//   - Méthode utilisée + date d'expertise
//
// Branchement dans ArtworkEdit.jsx :
//   import ArtworkComparables from "../components/artwork/tabs/ArtworkComparables";
//   // Ajouter dans les tabs :
//   { id: "expertise", labelKey: "Expertise CUP" }
//   // Dans le rendu :
//   {activeTab === "expertise" && (
//     <ArtworkComparables
//       formData={formData}
//       setFormData={setFormData}
//       artworkId={artworkId}
//       readOnly={!canEdit}
//     />
//   )}
//
// SQL à exécuter dans Supabase :
//   ALTER TABLE artworks ADD COLUMN IF NOT EXISTS cup_comparables jsonb;
//   ALTER TABLE artworks ADD COLUMN IF NOT EXISTS expert_note text;
//   ALTER TABLE artworks ADD COLUMN IF NOT EXISTS expert_date date;
//   ALTER TABLE artworks ADD COLUMN IF NOT EXISTS expert_method text;

import React, { useState } from "react";
import { Plus, Trash2, Save, TrendingUp, AlertCircle, CheckCircle2 } from "lucide-react";
import { supabase } from "../../../lib/supabaseClient";
import { useTranslation } from "react-i18next";

const METHOD_VALUES = ["cup_alleges", "cup_complet", "cup_ifrs"];

const EMPTY_COMPARABLE = {
  id:           "",
  maison:       "",
  date:         "",
  titre:        "",
  prix_adjuge:  "",
  devise:       "EUR",
  ajustement:   "",
  note:         "",
};

function newComparable() {
  return { ...EMPTY_COMPARABLE, id: `c_${Date.now()}_${Math.random().toString(36).slice(2)}` };
}

function formatEur(val, locale) {
  if (!val && val !== 0) return "—";
  return new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val);
}

export default function ArtworkComparables({ formData, setFormData, artworkId, readOnly = false, canEdit }) {
  const { t, i18n } = useTranslation();
  const isReadOnly = canEdit !== undefined ? !canEdit : readOnly;

  // ── Récupérer les données depuis formData ────────────────────────────────
  const comparables = Array.isArray(formData?.cup_comparables)
    ? formData.cup_comparables
    : [];

  const expertValue  = formData?.valuation_estimated_value ?? "";
  const expertMethod = formData?.expert_method ?? "";
  const expertDate   = formData?.expert_date   ?? "";
  const expertNote   = formData?.expert_note   ?? "";

  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  // ── Helpers patch formData ───────────────────────────────────────────────
  const patch = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  const patchComparable = (idx, key, val) => {
    const updated = comparables.map((c, i) => i === idx ? { ...c, [key]: val } : c);
    patch("cup_comparables", updated);
  };

  const addComparable = () => {
    if (comparables.length >= 5) return;
    patch("cup_comparables", [...comparables, newComparable()]);
  };

  const removeComparable = (idx) => {
    patch("cup_comparables", comparables.filter((_, i) => i !== idx));
  };

  // ── Valeurs calculées ────────────────────────────────────────────────────
  const valNum    = parseFloat(String(expertValue).replace(/\s/g, "").replace(",", ".")) || null;
  const valLow    = valNum ? Math.round(valNum * 0.9)  : null;
  const valHigh   = valNum ? Math.round(valNum * 1.10) : null;

  // Moyenne des comparables
  const avgComparables = (() => {
    const prices = comparables
      .map(c => parseFloat(String(c.prix_adjuge).replace(/\s/g, "").replace(",", ".")))
      .filter(n => !isNaN(n) && n > 0);
    if (!prices.length) return null;
    return Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
  })();

  // ── Sauvegarde immédiate ─────────────────────────────────────────────────
const handleSave = async () => {
  if (!artworkId || isReadOnly) return;

  setSaving(true);
  setSaved(false);

  const payload = {
    cup_comparables: comparables,
    valuation_estimated_value: valNum,
    valuation_consensus: valNum,
    expert_method: expertMethod || null,
    expert_date: expertDate || null,
    expert_note: expertNote || null,
    last_estimation_date:
      expertDate || new Date().toISOString().split("T")[0],
  };

  const { data, error } = await supabase.rpc(
    "update_artwork_as_collaborator",
    {
      p_artwork_id: artworkId,
      p_payload: payload
    }
  );

  setSaving(false);

  if (!error && data?.success !== false) {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  } else {
    console.error("[ArtworkComparables] save error:", error || data);
    alert(t("artworkComparables.saveError", {
      message: error?.message || data?.error || t("artworkComparables.unknownError"),
    }));
  }
};

  // ── Rendu ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8">

      {/* ── En-tête ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-serif">{t("artworkComparables.title")}</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {t("artworkComparables.subtitle")}
          </p>
        </div>
        {!isReadOnly && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : saved ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saved ? t("artworkComparables.saved") : t("artworkComparables.save")}
          </button>
        )}
      </div>

      {/* ── Valeur vénale ── */}
      <div className="border border-gray-200 rounded-xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          {t("artworkComparables.expertValueSection")}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("artworkComparables.estimatedValue")}
            </label>
            <input
              type="number"
              value={expertValue}
              onChange={e => patch("valuation_estimated_value", e.target.value)}
              disabled={isReadOnly}
              placeholder={t("artworkComparables.estimatedPlaceholder")}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("artworkComparables.method")}
            </label>
            <select
              value={expertMethod}
              onChange={e => patch("expert_method", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50"
            >
              <option value="">{t("artworkComparables.select")}</option>
              {METHOD_VALUES.map((value) => (
                <option key={value} value={value}>{t(`artworkComparables.methods.${value}`)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("artworkComparables.expertDate")}
            </label>
            <input
              type="date"
              value={expertDate}
              onChange={e => patch("expert_date", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50"
            />
          </div>
        </div>

        {/* Fourchette calculée */}
        {valNum && (
          <div className="flex items-center gap-4 pt-2 border-t border-gray-100">
            <TrendingUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="text-sm text-gray-700">
              {t("artworkComparables.range")} {" "}
              <span className="font-medium">{formatEur(valLow, i18n.language)}</span>
              {" "}—{" "}
              <span className="font-medium">{formatEur(valHigh, i18n.language)}</span>
            </div>
            {avgComparables && (
              <div className="text-sm text-gray-500 ml-auto">
                {t("artworkComparables.comparablesAverage")} <span className="font-medium">{formatEur(avgComparables, i18n.language)}</span>
                {Math.abs(valNum - avgComparables) / avgComparables > 0.3 && (
                  <span className="ml-2 text-amber-600 text-xs">
                    <AlertCircle className="w-3 h-3 inline mr-0.5" />
                    {t("artworkComparables.gapWarning")}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Comparables CUP ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            {t("artworkComparables.comparablesTitle", { count: comparables.length })}
          </h3>
          {!isReadOnly && comparables.length < 5 && (
            <button
              type="button"
              onClick={addComparable}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-black transition"
            >
              <Plus className="w-4 h-4" />
              {t("artworkComparables.addComparable")}
            </button>
          )}
        </div>

        {comparables.length === 0 && (
          <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
            <p className="text-sm text-gray-400">
              {t("artworkComparables.empty")}
            </p>
            {!isReadOnly && (
              <button
                type="button"
                onClick={addComparable}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:border-black transition"
              >
                <Plus className="w-4 h-4" />
                {t("artworkComparables.firstComparable")}
              </button>
            )}
          </div>
        )}

        {comparables.map((comp, idx) => (
          <div key={comp.id} className="border border-gray-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t("artworkComparables.comparable", { number: idx + 1 })}
              </span>
              {!isReadOnly && (
                <button type="button" onClick={() => removeComparable(idx)}>
                  <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500 transition" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t("artworkComparables.auctionHouse")}</label>
                <input
                  type="text"
                  value={comp.maison}
                  onChange={e => patchComparable(idx, "maison", e.target.value)}
                  disabled={isReadOnly}
                  placeholder={t("artworkComparables.auctionHousePlaceholder")}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t("artworkComparables.saleDate")}</label>
                <input
                  type="date"
                  value={comp.date}
                  onChange={e => patchComparable(idx, "date", e.target.value)}
                  disabled={isReadOnly}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("artworkComparables.soldArtwork")}</label>
              <input
                type="text"
                value={comp.titre}
                onChange={e => patchComparable(idx, "titre", e.target.value)}
                disabled={isReadOnly}
                placeholder={t("artworkComparables.soldArtworkPlaceholder")}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t("artworkComparables.hammerPrice")}</label>
                <input
                  type="number"
                  value={comp.prix_adjuge}
                  onChange={e => patchComparable(idx, "prix_adjuge", e.target.value)}
                  disabled={isReadOnly}
                  placeholder={t("artworkComparables.hammerPricePlaceholder")}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t("artworkComparables.adjustment")}</label>
                <input
                  type="number"
                  value={comp.ajustement}
                  onChange={e => patchComparable(idx, "ajustement", e.target.value)}
                  disabled={isReadOnly}
                  placeholder={t("artworkComparables.adjustmentPlaceholder")}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t("artworkComparables.adjustedValue")}</label>
                <div className="w-full border border-gray-100 rounded-lg px-3 py-1.5 text-sm bg-gray-50 text-gray-600">
                  {(() => {
                    const p = parseFloat(comp.prix_adjuge);
                    const a = parseFloat(comp.ajustement);
                    if (!p) return "—";
                    const adj = isNaN(a) ? 0 : a;
                    return formatEur(Math.round(p * (1 + adj / 100)), i18n.language);
                  })()}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("artworkComparables.adjustmentNote")}</label>
              <input
                type="text"
                value={comp.note}
                onChange={e => patchComparable(idx, "note", e.target.value)}
                disabled={isReadOnly}
                placeholder={t("artworkComparables.adjustmentNotePlaceholder")}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50"
              />
            </div>
          </div>
        ))}
      </div>

      {/* ── Note d'expert ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {t("artworkComparables.expertNote")}
        </label>
        <textarea
          value={expertNote}
          onChange={e => patch("expert_note", e.target.value)}
          disabled={isReadOnly}
          rows={4}
          placeholder={t("artworkComparables.expertNotePlaceholder")}
          className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50 resize-none"
        />
      </div>

      {/* ── Statut de complétude ── */}
      <div className={`flex items-center gap-3 p-4 rounded-xl border ${
        comparables.length >= 3 && valNum
          ? "border-emerald-200 bg-emerald-50"
          : "border-amber-200 bg-amber-50"
      }`}>
        {comparables.length >= 3 && valNum ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        ) : (
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
        )}
        <p className="text-sm text-gray-700">
          {comparables.length >= 3 && valNum
            ? t("artworkComparables.complete")
            : t("artworkComparables.incomplete", {
                reason: !valNum
                  ? t("artworkComparables.missingValue")
                  : t("artworkComparables.missingComparables", { count: 3 - comparables.length }),
              })
          }
        </p>
      </div>

    </div>
  );
}